<template>
  <Layout>
    <div class="schedule-container">
      <div class="container-fluid">
        <div class="row">
          <div class="col-12">
            <h2 class="page-title">Conference Schedule</h2>
            
            <!-- 会议选择区域 -->
            <div class="session-selection-section mb-4">
              <div class="card">
                <div class="card-header">
                  <h5>Select Conference</h5>
                </div>
                <div class="card-body">
                  <div class="row">
                    <div class="col-md-6">
                      <div class="form-group">
                        <label for="sessionSelect">Conference *</label>
                        <select 
                          class="form-control" 
                          id="sessionSelect" 
                          v-model="selectedSessionId"
                          @change="onSessionChange"
                          required
                        >
                          <option value="">{{ loadingSessions ? 'Loading conferences...' : 'Select a conference' }}</option>
                          <option v-for="session in sessionList" :key="session.id" :value="session.id">
                            {{ session.session_no }} - {{ session.title }}
                          </option>
                        </select>
                        <small v-if="loadingSessions" class="form-text text-muted">
                          Loading available conferences...
                        </small>
                      </div>
                    </div>
                    <div class="col-md-6">
                      <div class="session-info" v-if="selectedSession">
                        <h6>Conference Information</h6>
                        <div class="info-grid">
                          <div class="info-item">
                            <strong>Code:</strong> {{ selectedSession.session_no }}
                          </div>
                          <div class="info-item">
                            <strong>Title:</strong> {{ selectedSession.title }}
                          </div>
                          <div class="info-item">
                            <strong>Duration:</strong> {{ selectedSession.duration || 'TBD' }}
                          </div>
                          <div class="info-item">
                            <strong>Room:</strong> {{ selectedSession.room || 'TBD' }}
                          </div>
                          <div class="info-item">
                            <strong>Time:</strong> {{ selectedSession.time || 'TBD' }}
                          </div>
                          <div class="info-item">
                            <strong>Abstracts:</strong> {{ selectedSession.ab_num || 0 }}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- 日程展示区域 -->
            <div v-if="selectedSessionId && !loadingSchedule" class="schedule-display-section">
              <div class="card">
                <div class="card-header d-flex justify-content-between align-items-center">
                  <h5>Conference Schedule</h5>
                  <div class="schedule-controls">
                    <button 
                      class="btn btn-outline-primary btn-sm"
                      @click="refreshSchedule"
                      :disabled="loadingSchedule"
                    >
                      <i class="fa fa-refresh"></i> Refresh
                    </button>
                  </div>
                </div>
                <div class="card-body">
                  <!-- 加载状态 -->
                  <div v-if="loadingSchedule" class="loading-container">
                    <div class="spinner-border text-primary" role="status">
                      <span class="sr-only">Loading...</span>
                    </div>
                    <p class="mt-2">Loading schedule...</p>
                  </div>

                  <!-- 日程内容 -->
                  <div v-else-if="scheduleData && scheduleData.length > 0" class="schedule-content">
                    <div class="schedule-timeline">
                      <div 
                        v-for="(item, index) in scheduleData" 
                        :key="index"
                        class="schedule-item"
                      >
                        <div class="time-slot">
                          <div class="time">{{ item.start_time || 'TBD' }}</div>
                          <div class="duration">{{ item.duration || 'TBD' }}</div>
                        </div>
                        <div class="content">
                          <div class="session-title">{{ item.title || 'Untitled Session' }}</div>
                          <div class="session-details">
                            <span class="room" v-if="item.room">
                              <i class="fa fa-map-marker"></i> {{ item.room }}
                            </span>
                            <span class="type" v-if="item.type">
                              <i class="fa fa-tag"></i> {{ item.type }}
                            </span>
                            <span class="speaker" v-if="item.speaker">
                              <i class="fa fa-user"></i> {{ item.speaker }}
                            </span>
                          </div>
                          <div class="description" v-if="item.description">
                            {{ item.description }}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  <!-- 无数据状态 -->
                  <div v-else class="no-data">
                    <div class="text-center">
                      <i class="fa fa-calendar-times fa-3x text-muted mb-3"></i>
                      <h5>No Schedule Available</h5>
                      <p class="text-muted">
                        The schedule for this conference has not been published yet.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- 选择提示 -->
            <div v-if="!selectedSessionId" class="selection-prompt">
              <div class="card">
                <div class="card-body text-center">
                  <i class="fa fa-calendar fa-3x text-muted mb-3"></i>
                  <h5>Select a Conference</h5>
                  <p class="text-muted">
                    Please select a conference from the dropdown above to view its schedule.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </Layout>
</template>

<script>

import Layout from '@/components/Layout.vue'
import { apiService } from '@/config/api'

export default {
  name: 'Schedule',
  components: {
    Layout
  },
  data() {
    return {
      selectedSessionId: '',
      sessionList: [],
      loadingSessions: false,
      loadingSchedule: false,
      scheduleData: [],
      selectedSession: null
    }
  },
  created() {
    this.getSessionList()
  },
  methods: {
    // 获取会议列表
    async getSessionList() {
      this.loadingSessions = true
      try {
        const response = await apiService.sessions.getSessionList({ page: 1, limit: 100 })
        
        if (response.data && response.data.code === 1) {
          const apiData = response.data.data
          if (apiData && apiData.list && Array.isArray(apiData.list)) {
            this.sessionList = apiData.list
          } else if (Array.isArray(apiData)) {
            this.sessionList = apiData
          } else {
            console.error('API返回的数据格式不正确:', apiData)
            this.sessionList = []
          }
        } else {
          console.error('获取会议列表失败:', response.data)
          this.sessionList = []
        }
      } catch (error) {
        console.error('获取会议列表出错:', error)
        this.sessionList = []
      } finally {
        this.loadingSessions = false
      }
    },

    // 会议选择变化处理
    onSessionChange() {
      if (this.selectedSessionId) {
        this.selectedSession = this.sessionList.find(s => s.id === this.selectedSessionId)
        this.getSchedule()
      } else {
        this.selectedSession = null
        this.scheduleData = []
      }
    },

    // 获取日程数据
    async getSchedule() {
      if (!this.selectedSessionId) return
      
      this.loadingSchedule = true
      try {
        // 这里需要根据实际的API接口来调用
        // 暂时使用模拟数据
        // await this.getMockSchedule()
        const response = await apiService.schedule.getList({  session_id: this.selectedSessionId })
        if (response.data && response.data.code === 1) {
          this.scheduleData = response.data.data.scheduleData
        } else {
          console.error('获取日程数据失败:', response.data)
          this.scheduleData = []
        }
      } catch (error) {
        console.error('获取日程数据出错:', error)
        this.scheduleData = []
      } finally {
        this.loadingSchedule = false
      }
    },

    // 模拟获取日程数据（实际项目中应该调用真实API）
    async getMockSchedule() {
      // 模拟API延迟
      await new Promise(resolve => setTimeout(resolve, 1000))
      
      // 模拟日程数据
      this.scheduleData = [
        {
          start_time: '09:00',
          duration: '30 min',
          title: 'Opening Ceremony',
          room: 'Main Hall',
          type: 'Opening',
          speaker: 'Conference Chair',
          description: 'Welcome address and conference overview'
        },
        {
          start_time: '09:30',
          duration: '45 min',
          title: 'Keynote Speech: Climate Change Impact',
          room: 'Main Hall',
          type: 'Keynote',
          speaker: 'Dr. John Smith',
          description: 'Comprehensive overview of climate change impacts on Asia-Pacific region'
        },
        {
          start_time: '10:15',
          duration: '15 min',
          title: 'Coffee Break',
          room: 'Lobby',
          type: 'Break',
          speaker: '',
          description: 'Networking and refreshments'
        },
        {
          start_time: '10:30',
          duration: '60 min',
          title: 'Parallel Session A: Atmospheric Sciences',
          room: 'Room 101',
          type: 'Parallel Session',
          speaker: 'Session Chair: Dr. Li Wei',
          description: 'Presentations on atmospheric sciences research'
        },
        {
          start_time: '10:30',
          duration: '60 min',
          title: 'Parallel Session B: Ocean Sciences',
          room: 'Room 102',
          type: 'Parallel Session',
          speaker: 'Session Chair: Dr. Maria Garcia',
          description: 'Presentations on ocean sciences research'
        },
        {
          start_time: '11:30',
          duration: '30 min',
          title: 'Poster Session',
          room: 'Exhibition Hall',
          type: 'Poster',
          speaker: 'All Presenters',
          description: 'Interactive poster presentations and discussions'
        },
        {
          start_time: '12:00',
          duration: '60 min',
          title: 'Lunch Break',
          room: 'Dining Hall',
          type: 'Break',
          speaker: '',
          description: 'Lunch and networking'
        },
        {
          start_time: '13:00',
          duration: '90 min',
          title: 'Panel Discussion: Future of Geosciences',
          room: 'Main Hall',
          type: 'Panel',
          speaker: 'Panel Moderator: Prof. Chen Ming',
          description: 'Expert panel discussion on the future direction of geosciences research'
        },
        {
          start_time: '14:30',
          duration: '15 min',
          title: 'Coffee Break',
          room: 'Lobby',
          type: 'Break',
          speaker: '',
          description: 'Networking and refreshments'
        },
        {
          start_time: '14:45',
          duration: '75 min',
          title: 'Closing Session',
          room: 'Main Hall',
          type: 'Closing',
          speaker: 'Conference Chair',
          description: 'Summary and closing remarks'
        }
      ]
    },

    // 刷新日程
    refreshSchedule() {
      this.getSchedule()
    }
  }
}
</script>

<style scoped>
.schedule-container {
  padding: 20px 0;
  background-color: #f8f9fa;
  min-height: 100vh;
}

.page-title {
  color: #333;
  margin-bottom: 30px;
  font-weight: 600;
}

.session-selection-section {
  background: white;
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0,0,0,0.1);
}

.session-info {
  padding: 15px;
  background-color: #f8f9fa;
  border-radius: 6px;
  border-left: 4px solid #3498DB;
}

.info-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 8px;
  font-size: 14px;
}

.info-item {
  padding: 4px 0;
}

.schedule-display-section {
  margin-top: 20px;
}

.loading-container {
  text-align: center;
  padding: 40px;
}

.schedule-timeline {
  position: relative;
}

.schedule-timeline::before {
  content: '';
  position: absolute;
  left: 80px;
  top: 0;
  bottom: 0;
  width: 2px;
  background-color: #e9ecef;
}

.schedule-item {
  display: flex;
  margin-bottom: 20px;
  position: relative;
}

.schedule-item::before {
  content: '';
  position: absolute;
  left: 76px;
  top: 15px;
  width: 10px;
  height: 10px;
  border-radius: 50%;
  background-color: #3498DB;
  border: 2px solid white;
  box-shadow: 0 0 0 2px #3498DB;
}

.time-slot {
  width: 80px;
  text-align: center;
  padding-right: 20px;
  flex-shrink: 0;
}

.time {
  font-weight: bold;
  color: #3498DB;
  font-size: 16px;
}

.duration {
  font-size: 12px;
  color: #666;
  margin-top: 2px;
}

.content {
  flex: 1;
  background: white;
  padding: 15px;
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0,0,0,0.1);
  margin-left: 20px;
}

.session-title {
  font-weight: bold;
  color: #333;
  margin-bottom: 8px;
  font-size: 16px;
}

.session-details {
  display: flex;
  flex-wrap: wrap;
  gap: 15px;
  margin-bottom: 8px;
  font-size: 14px;
  color: #666;
}

.session-details span {
  display: flex;
  align-items: center;
  gap: 5px;
}

.session-details i {
  color: #3498DB;
}

.description {
  font-size: 14px;
  color: #666;
  line-height: 1.5;
  margin-top: 8px;
}

.no-data {
  padding: 40px;
}

.selection-prompt {
  margin-top: 20px;
}

.selection-prompt .card {
  background: white;
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0,0,0,0.1);
}

.schedule-controls {
  display: flex;
  gap: 10px;
}

/* 响应式设计 */
@media (max-width: 768px) {
  .schedule-container {
    padding: 15px 0;
  }
  
  .info-grid {
    grid-template-columns: 1fr;
  }
  
  .schedule-timeline::before {
    left: 60px;
  }
  
  .schedule-item::before {
    left: 56px;
  }
  
  .time-slot {
    width: 60px;
    padding-right: 15px;
  }
  
  .content {
    margin-left: 15px;
  }
  
  .session-details {
    flex-direction: column;
    gap: 8px;
  }
}
</style> 